import { initDB } from './indexedDB';
import type { IDBPDatabase } from 'idb';

// Store names
const ANGKASA_MEMBERS_STORE = "angkasa_members";
const ANGKASA_PROMOTIONS_STORE = "angkasa_promotions";
const ANGKASA_PURCHASES_STORE = "angkasa_purchases";

// Initialize the ANGKASA member database
export const initializeAngkasaMemberDatabase = async (): Promise<void> => {
  try {
    console.log("Initializing ANGKASA member database stores...");
    await initDB();
    
    // Verify data exists or add mock data
    await addMockDataIfEmpty();
    console.log("ANGKASA member database initialized successfully");
  } catch (error) {
    console.error("Failed to initialize ANGKASA member database:", error);
    throw error;
  }
};

// Add mock data for ANGKASA members if the store is empty
export const addMockDataIfEmpty = async (): Promise<void> => {
  try {
    // Check if we have existing members
    const members = await getAllMembers().catch(() => []);
    
    if (members.length === 0) {
      console.log("Adding mock ANGKASA members...");
      // Add members one by one to avoid transaction limits
      for (const member of mockMembers) {
        await saveMember(member).catch(err => console.error("Error saving member:", err));
      }
      
      console.log("Adding mock ANGKASA promotions...");
      for (const promotion of mockPromotions) {
        await savePromotion(promotion).catch(err => console.error("Error saving promotion:", err));
      }
      
      console.log("Adding mock ANGKASA purchases...");
      for (const purchase of mockPurchases) {
        await savePurchase(purchase).catch(err => console.error("Error saving purchase:", err));
      }
    } else {
      console.log("ANGKASA member data already exists, skipping mock data addition");
    }
  } catch (error) {
    console.error("Error adding mock data:", error);
    throw error;
  }
};

// Member operations
export const saveMember = async (member: AngkasaMember): Promise<void> => {
  try {
    const db = await initDB();
    const tx = db.transaction(ANGKASA_MEMBERS_STORE, 'readwrite');
    const store = tx.objectStore(ANGKASA_MEMBERS_STORE);
    
    // Add timestamp for sync purposes
    const memberWithTimestamp = {
      ...member,
      lastModified: new Date().getTime(),
      needsSync: true
    };
    
    await store.put(memberWithTimestamp);
    await tx.done;
  } catch (error) {
    console.error("Error saving member:", error);
    throw error;
  }
};

export const getMember = async (memberId: string): Promise<AngkasaMember | null> => {
  try {
    const db = await initDB();
    const tx = db.transaction(ANGKASA_MEMBERS_STORE, 'readonly');
    const store = tx.objectStore(ANGKASA_MEMBERS_STORE);
    const member = await store.get(memberId);
    await tx.done;
    return member || null;
  } catch (error) {
    console.error("Error getting member:", error);
    throw error;
  }
};

export const getAllMembers = async (): Promise<AngkasaMember[]> => {
  try {
    const db = await initDB();
    const tx = db.transaction(ANGKASA_MEMBERS_STORE, 'readonly');
    const store = tx.objectStore(ANGKASA_MEMBERS_STORE);
    const members = await store.getAll();
    await tx.done;
    return members || [];
  } catch (error) {
    console.error("Error getting all members:", error);
    throw error;
  }
};

export const deleteMember = async (memberId: string): Promise<void> => {
  try {
    const db = await initDB();
    const tx = db.transaction(ANGKASA_MEMBERS_STORE, 'readwrite');
    const store = tx.objectStore(ANGKASA_MEMBERS_STORE);
    await store.delete(memberId);
    await tx.done;
  } catch (error) {
    console.error("Error deleting member:", error);
    throw error;
  }
};

// Promotion operations
export const savePromotion = async (promotion: AngkasaPromotion): Promise<void> => {
  try {
    const db = await initDB();
    const tx = db.transaction(ANGKASA_PROMOTIONS_STORE, 'readwrite');
    const store = tx.objectStore(ANGKASA_PROMOTIONS_STORE);
    
    // Add timestamp for sync purposes
    const promotionWithTimestamp = {
      ...promotion,
      lastModified: new Date().getTime(),
      needsSync: true
    };
    
    await store.put(promotionWithTimestamp);
    await tx.done;
  } catch (error) {
    console.error("Error saving promotion:", error);
    throw error;
  }
};

export const getAllPromotions = async (): Promise<AngkasaPromotion[]> => {
  try {
    const db = await initDB();
    const tx = db.transaction(ANGKASA_PROMOTIONS_STORE, 'readonly');
    const store = tx.objectStore(ANGKASA_PROMOTIONS_STORE);
    const promotions = await store.getAll();
    await tx.done;
    return promotions || [];
  } catch (error) {
    console.error("Error getting all promotions:", error);
    throw error;
  }
};

// Purchase operations
export const savePurchase = async (purchase: AngkasaPurchase): Promise<void> => {
  try {
    const db = await initDB();
    const tx = db.transaction(ANGKASA_PURCHASES_STORE, 'readwrite');
    const store = tx.objectStore(ANGKASA_PURCHASES_STORE);
    
    const purchaseWithTimestamp = {
      ...purchase,
      lastModified: new Date().getTime(),
      needsSync: true
    };
    
    await store.put(purchaseWithTimestamp);
    await tx.done;
  } catch (error) {
    console.error("Error saving purchase:", error);
    throw error;
  }
};

export const getMemberPurchases = async (memberId: string): Promise<AngkasaPurchase[]> => {
  try {
    const db = await initDB();
    const tx = db.transaction(ANGKASA_PURCHASES_STORE, 'readonly');
    const store = tx.objectStore(ANGKASA_PURCHASES_STORE);
    const index = store.index("memberId");
    const purchases = await index.getAll(memberId);
    await tx.done;
    return purchases || [];
  } catch (error) {
    console.error("Error getting member purchases:", error);
    throw error;
  }
};

// Types
export interface AngkasaMember {
  memberId: string;
  name: string;
  icNumber: string;
  mobileNumber: string;
  email: string;
  memberType: "Primary" | "Secondary";
  cooperativeName: string;
  joinDate: string;
  validUntil: string;
  totalSpent: number;
  lastPurchase: string | null;
  promotionGroup: string;
  needsSync?: boolean;
  lastModified?: number;
}

export interface AngkasaPromotion {
  id: string;
  name: string;
  description: string;
  startDate: string;
  endDate: string;
  cooperativeName: string | null; // If null, available to all cooperatives
  discountType: "Percentage" | "Fixed";
  discountValue: number;
  minimumSpend: number;
  productCategory: string | null; // If null, applicable to all products
  maxRedemptions: number;
  currentRedemptions: number;
  memberGroup: string | null; // Target specific member groups
  needsSync?: boolean;
  lastModified?: number;
}

export interface AngkasaPurchase {
  id: string;
  memberId: string;
  date: string;
  amount: number;
  items: PurchaseItem[];
  storeId: string;
  promoApplied: string | null;
  promoSavings: number;
  needsSync?: boolean;
  lastModified?: number;
}

export interface PurchaseItem {
  productId: string;
  productName: string;
  quantity: number;
  unitPrice: number;
  total: number;
}

// Mock data for ANGKASA members
export const mockMembers: AngkasaMember[] = [
  {
    memberId: "COOP-10001",
    name: "Ahmad Bin Abdullah",
    icNumber: "881212-10-5523",
    mobileNumber: "012-3456789",
    email: "ahmad@example.com",
    memberType: "Primary",
    cooperativeName: "Retail Cooperative A",
    joinDate: "2022-01-15",
    validUntil: "2025-01-14",
    totalSpent: 1250.75,
    lastPurchase: "2025-05-10",
    promotionGroup: "Gold"
  },
  {
    memberId: "COOP-10002",
    name: "Siti Binti Mohamed",
    icNumber: "901023-14-5567",
    mobileNumber: "019-8765432",
    email: "siti@example.com",
    memberType: "Primary",
    cooperativeName: "School Cooperative B",
    joinDate: "2021-08-22",
    validUntil: "2024-08-21",
    totalSpent: 980.50,
    lastPurchase: "2025-05-01",
    promotionGroup: "Silver"
  },
  {
    memberId: "COOP-10003",
    name: "Rajesh Kumar",
    icNumber: "850504-08-7722",
    mobileNumber: "017-9876543",
    email: "rajesh@example.com",
    memberType: "Secondary",
    cooperativeName: "Agricultural Cooperative C",
    joinDate: "2023-03-10",
    validUntil: "2026-03-09",
    totalSpent: 430.25,
    lastPurchase: "2025-04-28",
    promotionGroup: "Regular"
  },
  {
    memberId: "COOP-10004",
    name: "Nurul Hakimah",
    icNumber: "920708-12-5511",
    mobileNumber: "013-7654321",
    email: "nurul@example.com",
    memberType: "Primary",
    cooperativeName: "Retail Cooperative A",
    joinDate: "2020-06-15",
    validUntil: "2023-06-14",
    totalSpent: 2150.00,
    lastPurchase: "2025-05-15",
    promotionGroup: "Gold"
  },
  {
    memberId: "COOP-10005",
    name: "Lee Wei Ming",
    icNumber: "870212-11-5233",
    mobileNumber: "016-1234567",
    email: "weiming@example.com",
    memberType: "Primary",
    cooperativeName: "School Cooperative B",
    joinDate: "2021-09-05",
    validUntil: "2024-09-04",
    totalSpent: 750.30,
    lastPurchase: "2025-05-02",
    promotionGroup: "Silver"
  },
  {
    memberId: "COOP-10006",
    name: "Maria Gonzalez",
    icNumber: "910310-14-2288",
    mobileNumber: "018-9876543",
    email: "maria@example.com",
    memberType: "Secondary",
    cooperativeName: "Agricultural Cooperative C",
    joinDate: "2022-11-20",
    validUntil: "2025-11-19",
    totalSpent: 120.75,
    lastPurchase: "2025-04-15",
    promotionGroup: "Regular"
  },
  {
    memberId: "COOP-10007",
    name: "Kumaravel Subramaniam",
    icNumber: "860728-09-4455",
    mobileNumber: "011-23456789",
    email: "kumar@example.com",
    memberType: "Primary",
    cooperativeName: "Retail Cooperative A",
    joinDate: "2023-02-18",
    validUntil: "2026-02-17",
    totalSpent: 1850.25,
    lastPurchase: "2025-05-12",
    promotionGroup: "Gold"
  }
];

// Mock data for ANGKASA promotions
export const mockPromotions: AngkasaPromotion[] = [
  {
    id: "PROMO-1001",
    name: "Raya Special Discount",
    description: "Special 10% discount for all coop members during Raya season",
    startDate: "2025-04-01",
    endDate: "2025-05-31",
    cooperativeName: null,
    discountType: "Percentage",
    discountValue: 10,
    minimumSpend: 100,
    productCategory: null,
    maxRedemptions: 1000,
    currentRedemptions: 325,
    memberGroup: null
  },
  {
    id: "PROMO-1002",
    name: "School Supplies Discount",
    description: "15% off all school supplies for School Cooperative members",
    startDate: "2025-01-01",
    endDate: "2025-12-31",
    cooperativeName: "School Cooperative B",
    discountType: "Percentage",
    discountValue: 15,
    minimumSpend: 50,
    productCategory: "School Supplies",
    maxRedemptions: 500,
    currentRedemptions: 178,
    memberGroup: "Silver"
  },
  {
    id: "PROMO-1003",
    name: "Gold Member Exclusive",
    description: "RM20 off for Gold tier members on all purchases",
    startDate: "2025-01-01",
    endDate: "2025-12-31",
    cooperativeName: null,
    discountType: "Fixed",
    discountValue: 20,
    minimumSpend: 200,
    productCategory: null,
    maxRedemptions: 200,
    currentRedemptions: 87,
    memberGroup: "Gold"
  }
];

// Mock data for ANGKASA purchases
export const mockPurchases: AngkasaPurchase[] = [
  {
    id: "PUR-10001",
    memberId: "COOP-10001",
    date: "2025-05-10",
    amount: 150.75,
    items: [
      {
        productId: "PROD-001",
        productName: "Rice 5kg",
        quantity: 1,
        unitPrice: 25.90,
        total: 25.90
      },
      {
        productId: "PROD-002",
        productName: "Cooking Oil 5L",
        quantity: 2,
        unitPrice: 45.50,
        total: 91.00
      },
      {
        productId: "PROD-003",
        productName: "Eggs 30pcs",
        quantity: 1,
        unitPrice: 15.90,
        total: 15.90
      }
    ],
    storeId: "STORE-001",
    promoApplied: "PROMO-1001",
    promoSavings: 16.75
  },
  {
    id: "PUR-10002",
    memberId: "COOP-10001",
    date: "2025-04-15",
    amount: 85.20,
    items: [
      {
        productId: "PROD-004",
        productName: "Chicken 1kg",
        quantity: 2,
        unitPrice: 22.90,
        total: 45.80
      },
      {
        productId: "PROD-005",
        productName: "Vegetables Assorted",
        quantity: 1,
        unitPrice: 15.40,
        total: 15.40
      }
    ],
    storeId: "STORE-001",
    promoApplied: null,
    promoSavings: 0
  },
  {
    id: "PUR-10003",
    memberId: "COOP-10002",
    date: "2025-05-01",
    amount: 120.50,
    items: [
      {
        productId: "PROD-006",
        productName: "Notebooks Set",
        quantity: 3,
        unitPrice: 12.50,
        total: 37.50
      },
      {
        productId: "PROD-007",
        productName: "Pencil Case",
        quantity: 1,
        unitPrice: 15.00,
        total: 15.00
      },
      {
        productId: "PROD-008",
        productName: "Reference Books",
        quantity: 2,
        unitPrice: 34.00,
        total: 68.00
      }
    ],
    storeId: "STORE-002",
    promoApplied: "PROMO-1002",
    promoSavings: 18.08
  }
];
